/**
 * storageService — IndexedDB en lugar de localStorage
 *
 * ¿Por qué IndexedDB?
 *  - localStorage tiene límite de ~5MB y bloquea el hilo principal
 *  - IndexedDB puede almacenar cientos de MB (fotos, segmentos grandes)
 *  - NO se borra cuando el usuario "limpia caché del navegador" normal
 *  - Funciona perfectamente en modo offline
 *
 * Estructura de la base de datos:
 *   DB: ControlCampoDB
 *   Store: formData   → datos del formulario activo
 *   Store: segments   → segmentos capturados offline (pueden ser +5)
 *   Store: offlineQueue → peticiones pendientes de enviar al servidor
 */

const DB_NAME = 'ControlCampoDB';
const DB_VERSION = 3; // v3: agrega inspectorActionQueue para acciones offline con fotos

// Abrimos (o creamos) la base de datos una sola vez
function openDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      // Store para datos generales del formulario
      if (!db.objectStoreNames.contains('formData')) {
        db.createObjectStore('formData', { keyPath: 'id' });
      }

      // Store para segmentos capturados en campo (offline)
      if (!db.objectStoreNames.contains('segments')) {
        const segStore = db.createObjectStore('segments', {
          keyPath: 'id',
          autoIncrement: true
        });
        segStore.createIndex('by_report', 'reportId', { unique: false });
        segStore.createIndex('by_synced', 'synced', { unique: false });
      }

      // Store para peticiones que fallaron y deben reintentarse
      if (!db.objectStoreNames.contains('offlineQueue')) {
        db.createObjectStore('offlineQueue', {
          keyPath: 'id',
          autoIncrement: true
        });
      }

      // Store para segmentos capturados por Inspectores (offline PE / PI)
      if (!db.objectStoreNames.contains('inspectorSegments')) {
        const inspStore = db.createObjectStore('inspectorSegments', {
          keyPath: 'id',
          autoIncrement: true
        });
        inspStore.createIndex('by_workday', 'workdayId', { unique: false });
        inspStore.createIndex('by_synced', 'synced', { unique: false });
      }

      // Store para acciones del inspector guardadas offline (aprobar/rechazar incidencia, validar PI)
      // Las fotos se serializan como ArrayBuffer para poder almacenarse en IndexedDB
      if (!db.objectStoreNames.contains('inspectorActionQueue')) {
        db.createObjectStore('inspectorActionQueue', {
          keyPath: 'id',
          autoIncrement: true
        });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

// ─────────────────────────────────────────────
// Helper genérico para leer / escribir
// ─────────────────────────────────────────────
async function dbGet(storeName, key) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly');
    const req = tx.objectStore(storeName).get(key);
    req.onsuccess = () => resolve(req.result ?? null);
    req.onerror = () => reject(req.error);
  });
}

async function dbGetAll(storeName) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly');
    const req = tx.objectStore(storeName).getAll();
    req.onsuccess = () => resolve(req.result ?? []);
    req.onerror = () => reject(req.error);
  });
}

async function dbPut(storeName, value) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const req = tx.objectStore(storeName).put(value);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function dbDelete(storeName, key) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const req = tx.objectStore(storeName).delete(key);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

async function dbClear(storeName) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const req = tx.objectStore(storeName).clear();
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

// ─────────────────────────────────────────────
// API PÚBLICA — Equivalente al storageService anterior
// Los métodos son async porque IndexedDB es asíncrono
// ─────────────────────────────────────────────
export const storageService = {
  // ── Formulario activo ──────────────────────
  saveFormData: async (data) => {
    try {
      await dbPut('formData', { id: 'current', ...data });
    } catch (error) {
      console.error('[StorageService] Error guardando formData:', error);
    }
  },

  getFormData: async () => {
    try {
      return await dbGet('formData', 'current');
    } catch (error) {
      console.error('[StorageService] Error leyendo formData:', error);
      return null;
    }
  },

  clearFormData: async () => {
    try {
      await dbDelete('formData', 'current');
    } catch (error) {
      console.error('[StorageService] Error borrando formData:', error);
    }
  },

  // ── Segmentos offline ──────────────────────
  saveSegment: async (segmentData) => {
    try {
      const id = await dbPut('segments', {
        ...segmentData,
        synced: false,
        createdAt: new Date().toISOString()
      });
      return id;
    } catch (error) {
      console.error('[StorageService] Error guardando segmento:', error);
      return null;
    }
  },

  getAllSegments: async () => {
    try {
      return await dbGetAll('segments');
    } catch (error) {
      console.error('[StorageService] Error leyendo segmentos:', error);
      return [];
    }
  },

  getPendingSegments: async () => {
    try {
      const all = await dbGetAll('segments');
      return all.filter((s) => !s.synced);
    } catch (error) {
      console.error('[StorageService] Error leyendo segmentos pendientes:', error);
      return [];
    }
  },

  markSegmentSynced: async (id) => {
    try {
      const segment = await dbGet('segments', id);
      if (segment) {
        await dbPut('segments', { ...segment, synced: true });
      }
    } catch (error) {
      console.error('[StorageService] Error marcando segmento como sincronizado:', error);
    }
  },

  deleteSegment: async (id) => {
    try {
      await dbDelete('segments', id);
    } catch (error) {
      console.error('[StorageService] Error borrando segmento:', error);
    }
  },

  clearAllSegments: async () => {
    try {
      await dbClear('segments');
    } catch (error) {
      console.error('[StorageService] Error borrando todos los segmentos:', error);
    }
  },

  // ── Cola offline (peticiones fallidas) ────
  addToOfflineQueue: async (requestData) => {
    try {
      await dbPut('offlineQueue', {
        ...requestData,
        queuedAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('[StorageService] Error añadiendo a cola offline:', error);
    }
  },

  getOfflineQueue: async () => {
    try {
      return await dbGetAll('offlineQueue');
    } catch (error) {
      console.error('[StorageService] Error leyendo cola offline:', error);
      return [];
    }
  },

  removeFromOfflineQueue: async (id) => {
    try {
      await dbDelete('offlineQueue', id);
    } catch (error) {
      console.error('[StorageService] Error borrando item de cola offline:', error);
    }
  },

  clearOfflineQueue: async () => {
    try {
      await dbClear('offlineQueue');
    } catch (error) {
      console.error('[StorageService] Error limpiando cola offline:', error);
    }
  },

  // ── Segmentos Offline de Inspector (PE / PI) ──
  saveInspectorSegment: async (segmentData) => {
    try {
      let photo_buffer = null;
      let photo_name = 'pe_segment.jpg';
      let photo_type = 'image/jpeg';

      if (segmentData.photo_blob) {
        if (segmentData.photo_blob instanceof Blob) {
          try {
            photo_buffer = await segmentData.photo_blob.arrayBuffer();
            photo_name = segmentData.photo_blob.name || 'pe_segment.jpg';
            photo_type = segmentData.photo_blob.type || 'image/jpeg';
          } catch (bErr) {
            console.warn('[StorageService] Error convirtiendo photo_blob a ArrayBuffer:', bErr);
          }
        } else if (segmentData.photo_blob instanceof ArrayBuffer) {
          photo_buffer = segmentData.photo_blob;
        }
      }

      // Evitamos pasar el objeto File/Blob vivo a IndexedDB para prevenir DataCloneError
      const record = {
        ...segmentData,
        photo_blob: null,
        photo_buffer,
        photo_name,
        photo_type,
        synced: false,
        createdAt: new Date().toISOString()
      };

      const id = await dbPut('inspectorSegments', record);
      return id || Date.now();
    } catch (error) {
      console.error('[StorageService] Error guardando segmento de inspector:', error);
      return Date.now();
    }
  },

  getPendingInspectorSegments: async (workdayId) => {
    try {
      const all = await dbGetAll('inspectorSegments');
      const filtered = all.filter((s) => !s.synced && (workdayId ? String(s.workdayId) === String(workdayId) : true));
      return filtered.map((s) => {
        let reconstructedBlob = null;
        if (s.photo_buffer && s.photo_buffer.byteLength > 0) {
          try {
            reconstructedBlob = new File([s.photo_buffer], s.photo_name || 'pe_segment.jpg', {
              type: s.photo_type || 'image/jpeg'
            });
          } catch (e) {
            reconstructedBlob = new Blob([s.photo_buffer], { type: s.photo_type || 'image/jpeg' });
          }
        } else if (s.photo_blob && s.photo_blob instanceof Blob && s.photo_blob.size > 0) {
          reconstructedBlob = s.photo_blob;
        }
        return {
          ...s,
          photo_blob: reconstructedBlob
        };
      });
    } catch (error) {
      console.error('[StorageService] Error leyendo segmentos de inspector pendientes:', error);
      return [];
    }
  },

  deleteInspectorSegment: async (id) => {
    try {
      await dbDelete('inspectorSegments', id);
    } catch (error) {
      console.error('[StorageService] Error borrando segmento de inspector:', error);
    }
  },

  clearInspectorSegments: async (workdayId) => {
    try {
      const all = await dbGetAll('inspectorSegments');
      for (const s of all) {
        if (!workdayId || String(s.workdayId) === String(workdayId)) {
          await dbDelete('inspectorSegments', s.id);
        }
      }
    } catch (error) {
      console.error('[StorageService] Error limpiando segmentos de inspector:', error);
    }
  },

  // ── Cola de acciones offline (approve/reject incidencia, validate PI) ──
  //
  // Las fotos (File objects) NO se pueden guardar directamente en IndexedDB.
  // Las serializamos como ArrayBuffer + metadata { name, type, size }.
  // Al sincronizar, las reconstruimos como Blob y las agregamos al FormData.

  /**
   * Serializa un array de File objects a formato guardable en IndexedDB.
   * Retorna una Promise con el array serializado.
   */
  serializeFiles: async (files = []) => {
    const serialized = [];
    for (const file of files) {
      const buffer = await file.arrayBuffer();
      serialized.push({
        name: file.name,
        type: file.type || 'image/jpeg',
        size: file.size,
        buffer
      });
    }
    return serialized;
  },

  /**
   * Reconstruye File objects desde el formato serializado.
   * Retorna un array de File objects listos para agregar al FormData.
   */
  deserializeFiles: (serializedFiles = []) => {
    return serializedFiles.map(sf => {
      const blob = new Blob([sf.buffer], { type: sf.type });
      return new File([blob], sf.name, { type: sf.type });
    });
  },

  /**
   * Guarda una acción en la cola offline.
   * @param {object} action - { type, endpoint, method, fields, photos (File[]), label }
   */
  queueInspectorAction: async (action) => {
    try {
      const serializedPhotos = await storageService.serializeFiles(action.photos || []);
      const id = await dbPut('inspectorActionQueue', {
        type: action.type,           // 'approve_unrealizable' | 'reject_unrealizable' | 'validate_pi_segment'
        endpoint: action.endpoint,   // URL completa del endpoint
        method: action.method || 'POST',
        fields: action.fields || {}, // Campos de texto del FormData
        photos: serializedPhotos,    // Fotos serializadas como ArrayBuffer
        photoFieldName: action.photoFieldName || 'evidence_photos',
        label: action.label || 'Acción pendiente',
        queuedAt: new Date().toISOString(),
        retries: 0
      });
      console.log(`[ActionQueue] Acción guardada offline: ${action.label} (id=${id})`);
      return id;
    } catch (error) {
      console.error('[ActionQueue] Error guardando acción offline:', error);
      return null;
    }
  },

  /** Retorna todas las acciones pendientes de la cola */
  getPendingInspectorActions: async () => {
    try {
      return await dbGetAll('inspectorActionQueue');
    } catch (error) {
      console.error('[ActionQueue] Error leyendo cola de acciones:', error);
      return [];
    }
  },

  /** Elimina una acción de la cola por id (después de enviarla con éxito) */
  deleteInspectorAction: async (id) => {
    try {
      await dbDelete('inspectorActionQueue', id);
    } catch (error) {
      console.error('[ActionQueue] Error borrando acción de la cola:', error);
    }
  },

  /** Limpia toda la cola (por si se necesita reset) */
  clearInspectorActionQueue: async () => {
    try {
      await dbClear('inspectorActionQueue');
    } catch (error) {
      console.error('[ActionQueue] Error limpiando cola de acciones:', error);
    }
  }
};